/**
 * Shared machinery of the legacy importers (W1-08 formats-legacy-respack): official Phigros JSON (PGR, this folder)
 * and PhiEditer text (PEC, ../pec). Both reproduce Phira's keyframe construction exactly (formats-and-packaging.md
 * P§1.3, P§3.3, P§4.3) and then express the KEYFRAMES (not the raw events) as RPE IR events (DESIGN.md §7.4):
 *
 *   Phira keyframes (time, value, tween)  --forwardPlan-->  pieces [A, B)  --planToEvents-->  IrEvent list
 *
 * Forward play. Phira evaluates an `Anim` with a stateful cursor that only walks forward during playback: from a
 * fresh track the cursor sits on keyframe i once every keyframe 1..i has a time <= t. With E_i = max(t_1 .. t_i)
 * segment i (keyframe i -> i+1) is therefore active on [E_i, E_{i+1}) (segment 0 on (-inf, t_1)), and after the
 * last keyframe its value holds. For sorted keyframes this is the usual "last keyframe with time <= t"; for
 * keyframes out of time order (unsorted PGR events, unsorted PEC `cv`) it is what Phira shows when the chart plays
 * forward. Linear segments sliced by forward play stay linear, so they are represented exactly (up to f32 rounding
 * of the written values).
 *
 * Representation rules (DESIGN.md §6.3 lowering in reverse):
 *  - a segment becomes one event [A, B] (start = value at A, end = value at B, its easing);
 *  - a constant (hold) segment that continues the previous event's end value is left as a GAP (RPE holds it);
 *  - before the first event RPE extrapolates that event, which equals Phira's extrapolation of keyframe 0 whenever
 *    the first event IS segment 0; otherwise a constant lead event from beat 0 is prepended;
 *  - when the final value differs from the last event's end, a trailing constant event of one beat holds it;
 *  - no zero-length event and no empty list is ever produced.
 *
 * Also here: time points (exact beat + Phira seconds), bounded-rational beats for float times, the IR builders for
 * legacy lines/notes/META, the report collector, and the unit constants of both formats.
 */

import { Beats, I32_MAX, I32_MIN, U32_MAX } from '../../core/time/beat';
import type { Beat, TempoMap } from '../../core/time/types';
import type {
  ImportClass,
  ImportReportEntry,
  IrBpm,
  IrEvent,
  IrEventList,
  IrEventOrigin,
  IrExtended,
  IrLayer,
  IrLine,
  IrMeta,
  IrNote,
} from '../../core/ir/types';
import type { MessageRef, NonEmpty } from '../../core/util/types';
import { shortestF32 } from '../json/shortest';

const fr = Math.fround;

// ---------------------------------------------------------------------------------------------------------------
// Constants (P§1.6, P§3.4, P§4.3, D§7.4)
// ---------------------------------------------------------------------------------------------------------------

/**
 * scaleY written on every imported PGR / PEC line: Phira draws those lines 0.0076 thick (their scale-y anim is empty,
 * so the "y-scaled" check sees 0); in RPE only |scaleY - 1| > 1e-4 selects the 0.0076 thickness (P§1.2).
 */
export const LEGACY_THICKNESS_SCALE_Y = 1.0002;
/** PEC line length: Phira scales PE lines by 3.91/6; RPE scaleX on `line.png` lines is halved by Phira (P§4.3). */
export const PEC_LINE_SCALE_X = 3.91 / 3;
/** RPE META.RPEVersion written for imported legacy charts (speed is linear, so the version never matters). */
export const LEGACY_RPE_VERSION = 160;
/** judgeLineGroup of imported legacy charts: RPE's single default group (every line has Group 0; Phira ignores it). */
export const LEGACY_LINE_GROUPS: readonly string[] = ['Default'];
/** RPE `visibleTime` default (seconds; "always visible"). */
export const RPE_DEFAULT_VISIBLE_TIME = 999999;
/** Largest denominator tried when a float beat is stored as a fraction (DESIGN.md: placement uses 1/1920). */
export const BEAT_GRID = 1920;
/**
 * Range of imported beats. Times beyond it (huge or infinite source times) are clamped and reported. The upper
 * bound leaves room for the one-beat trailing events the importers append (the beat integer part is an i32).
 */
export const LEGACY_BEAT_MIN = I32_MIN;
export const LEGACY_BEAT_MAX = I32_MAX - 2;

// ---------------------------------------------------------------------------------------------------------------
// Time points and beats
// ---------------------------------------------------------------------------------------------------------------

/** A chart time as both the exact IR beat and the seconds Phira computes for it. */
export interface TimePoint {
  readonly sec: number;
  readonly beat: Beat;
}

export interface BeatOf {
  readonly beat: Beat;
  /** The beat's f64 value equals x exactly. */
  readonly exact: boolean;
  /** |beat - x| in beats (infinite for non-finite x). */
  readonly error: number;
  /** x was NaN, infinite or outside [LEGACY_BEAT_MIN, LEGACY_BEAT_MAX] and was clamped (NaN -> 0). */
  readonly clamped: boolean;
}

/** The clamped beat for a value outside the legacy range (NaN -> 0). */
function clampedBeat(x: number): BeatOf {
  const beat = Number.isNaN(x) ? Beats.ZERO : Beats.fromInt(x > 0 ? LEGACY_BEAT_MAX : LEGACY_BEAT_MIN);
  return { beat, exact: false, error: Number.isFinite(x) ? Math.abs(Beats.toF64(beat) - x) : Infinity, clamped: true };
}

function inLegacyRange(x: number): boolean {
  return x >= LEGACY_BEAT_MIN && x <= LEGACY_BEAT_MAX;
}

/**
 * The rational beat for an f64 beat value x: x itself when a rational with denominator <= 1920 equals it (up to one
 * f64 rounding step), otherwise the rational with a u32 denominator closest to x (exact for every dyadic value of
 * moderate size, e.g. every f32). Values that are not finite or lie outside the legacy beat range are clamped.
 */
export function beatOfF64(x: number): BeatOf {
  if (!Number.isFinite(x) || !inLegacyRange(x)) return clampedBeat(x);
  const nice = Beats.fromF64(x, BEAT_GRID);
  const niceErr = Math.abs(Beats.toF64(nice) - x);
  if (niceErr <= Math.abs(x) * Number.EPSILON)
    return { beat: nice, exact: niceErr === 0, error: niceErr, clamped: false };
  const fine = Beats.fromF64(x, U32_MAX);
  const fineErr = Math.abs(Beats.toF64(fine) - x);
  return { beat: fine, exact: fineErr === 0, error: fineErr, clamped: false };
}

/**
 * The beat for a value Phira reads as an f32 (PEC beats): the fraction with the smallest denominator <= 1920 whose
 * f64 value rounds to the same f32 (54.984 -> 6873/125, 0.33333334 -> 1/3), so the beat is one Phira cannot tell
 * apart from the written value; otherwise the f32 value itself (`beatOfF64`). Every result that reads back as its
 * f32 keeps distinct f32 values distinct and equal ones equal, so note times (and the multiple-note hint) match
 * Phira's; that holds for every fraction found here and every f32 down to about 2^-9 in magnitude (smaller ones need
 * a denominator above u32 and are rounded; callers report them as inexact). `cache` memoises the search per import.
 */
export function beatOfF32(x: number, cache?: Map<number, BeatOf>): BeatOf {
  const f = Math.fround(x);
  if (!Number.isFinite(f) || !inLegacyRange(f) || Number.isInteger(f)) return beatOfF64(f);
  const hit = cache?.get(f);
  if (hit !== undefined) return hit;
  let out: BeatOf | null = null;
  for (let d = 2; d <= BEAT_GRID && out === null; d++) {
    const p = Math.round(f * d);
    // Cheap filter on p / d; the beat's own f64 value (w + n / d, as Phira evaluates it) decides.
    if (Math.fround(p / d) !== f) continue;
    const beat = Beats.make(0, p, d);
    const v = Beats.toF64(beat);
    if (Math.fround(v) === f) out = { beat, exact: v === f, error: Math.abs(v - f), clamped: false };
  }
  const res = out ?? beatOfF64(f);
  cache?.set(f, res);
  return res;
}

/** a - b, clamped to the legacy beat range (`clamped` set) instead of leaving the i32 range. */
export function subBeats(a: Beat, b: Beat): { readonly beat: Beat; readonly clamped: boolean } {
  const x = Beats.toF64(a) - Beats.toF64(b);
  if (inLegacyRange(x)) {
    try {
      return { beat: Beats.sub(a, b), clamped: false };
    } catch {
      // Only at the very edge of the i32 range: clamped below.
    }
  }
  return { beat: clampedBeat(x).beat, clamped: true };
}

export interface SecPoint {
  readonly point: TimePoint;
  /** The point's seconds equal `sec` (to a few ulps). */
  readonly exact: boolean;
  /** `sec` was not finite or lies beyond the legacy beat range; the point is clamped. */
  readonly clamped: boolean;
}

/** `secPoint` grid for points that must land on Phira's own seconds (the max_time freeze): any u32 denominator. */
export const EXACT_TIME_GRID = U32_MAX;

/**
 * Time point of a chart-seconds value through the IR tempo: the simplest beat (denominator <= 1920) that reproduces
 * `sec`, else the nearest multiple of 1/`grid` (`exact` when it round-trips; with EXACT_TIME_GRID it practically
 * always does). The point's `sec` is the seconds of its beat, so heights computed from it agree with the
 * represented curve.
 */
export function secPoint(tempo: TempoMap, sec: number, grid: number = BEAT_GRID): SecPoint {
  const x = Number.isFinite(sec) ? tempo.beatF64At(sec) : sec;
  if (!Number.isFinite(x) || !inLegacyRange(x)) {
    const { beat } = clampedBeat(x);
    return { point: { sec: tempo.secAt(beat), beat }, exact: false, clamped: true };
  }
  let beat: Beat;
  try {
    beat = tempo.beatAt(sec, grid);
  } catch {
    // Degenerate tempo (e.g. an infinite seconds-per-beat): fall back to the plain beat value.
    beat = beatOfF64(x).beat;
  }
  const back = tempo.secAt(beat);
  return {
    point: { sec: back, beat },
    exact: Math.abs(back - sec) <= Math.abs(sec) * 4 * Number.EPSILON,
    clamped: false,
  };
}

/**
 * Phira's BpmList beats -> seconds, literally (rpe-format.md F§3.2): T_0 = 0, T_i = T_{i-1} + (b_i - b_{i-1}) *
 * (60 / bpm_{i-1}) in LIST order, and the lookup walks the list from its start and stops at the first point whose
 * beat lies after the given beat (k = the last point before that stop, 0 if none): T_k + (beats - b_k) *
 * (60 / bpm_k), in f64 and in this operation order. For a list in beat order this is exactly the tempo map
 * (core/time/tempo.ts); for an unsorted list, which Phira uses as written (extra.json `bpm`, PEC `bp` lines), the
 * walk never reaches a point behind a later beat, whereas "the last point at or before the beat anywhere in the
 * list" would (beats [0, 8, 4] at beat 6: Phira uses the point at 0, not the one at 4).
 */
export function phiraBpmSeconds(
  list: ReadonlyArray<{ readonly beat: number; readonly bpm: number }>,
): (beats: number) => number {
  const els: { readonly b: number; readonly t: number; readonly bpm: number }[] = [];
  for (const p of list) {
    const prev = els[els.length - 1];
    els.push({ b: p.beat, t: prev === undefined ? 0 : prev.t + (p.beat - prev.b) * (60 / prev.bpm), bpm: p.bpm });
  }
  return (beats) => {
    let k = 0;
    for (let i = 0; i < els.length; i++) {
      if ((els[i]?.b ?? Number.NaN) > beats) break;
      k = i;
    }
    const el = els[k];
    return el === undefined ? Number.NaN : el.t + (beats - el.b) * (60 / el.bpm);
  };
}

export function cmpTime(a: TimePoint, b: TimePoint): -1 | 0 | 1 {
  return Beats.cmp(a.beat, b.beat);
}

function maxTime(a: TimePoint, b: TimePoint): TimePoint {
  return cmpTime(b, a) > 0 ? b : a;
}

const BEAT_ZERO_POINT: TimePoint = Object.freeze({ sec: 0, beat: Beats.ZERO });

// ---------------------------------------------------------------------------------------------------------------
// Keyframes -> pieces (forward play)
// ---------------------------------------------------------------------------------------------------------------

/** Tween of a Phira keyframe: hold (y = 0), linear, or an RPE easing number (PEC `cm` / `cr`). */
export type KfTween = 'hold' | 'linear' | number;

export interface LegacyKeyframe {
  readonly time: TimePoint;
  readonly value: number;
  readonly tween: KfTween;
}

/** One stretch of a channel: [start, end) with the event that represents it. */
export interface Piece {
  readonly start: TimePoint;
  readonly end: TimePoint;
  readonly v0: number;
  readonly v1: number;
  /** RPE easingType of the event (1 for linear and constant pieces). */
  readonly easingType: number;
  readonly constant: boolean;
}

export interface PiecePlan {
  readonly pieces: Piece[];
  /** Value Phira shows before the first piece when it is not the first piece's extrapolation. */
  readonly lead: number | null;
  /** From `final.at` on, Phira holds `final.value`. */
  readonly final: { readonly at: TimePoint; readonly value: number };
  /** Phira shows NaN / infinity before the first keyframe (zero-length first segment with a non-hold tween). */
  readonly nanLead: boolean;
}

function easingOf(tween: KfTween): number {
  return typeof tween === 'number' ? tween : 1;
}

/**
 * Value of segment i (keyframes a -> b) at seconds t. Only linear and hold segments are ever sliced: eased
 * keyframes come from PEC, whose sanitize_events sorts them, so forward play never cuts into an eased segment.
 */
function segmentValueAt(a: LegacyKeyframe, b: LegacyKeyframe, t: number): number {
  if (a.tween === 'hold') return a.value;
  const dt = b.time.sec - a.time.sec;
  if (dt === 0) return b.value;
  return a.value + ((b.value - a.value) * (t - a.time.sec)) / dt;
}

function pieceOf(a: LegacyKeyframe, b: LegacyKeyframe, start: TimePoint, end: TimePoint): Piece {
  const exactSpan = Beats.eq(start.beat, a.time.beat) && Beats.eq(end.beat, b.time.beat);
  const hold = a.tween === 'hold';
  const v0 = hold ? a.value : exactSpan ? a.value : segmentValueAt(a, b, start.sec);
  const v1 = hold ? a.value : exactSpan ? b.value : segmentValueAt(a, b, end.sec);
  return {
    start,
    end,
    v0,
    v1,
    easingType: hold ? 1 : exactSpan ? easingOf(a.tween) : 1,
    constant: hold || fr(v0) === fr(v1),
  };
}

/** Phira's forward-play view of a keyframe list (module header). */
export function forwardPlan(kfs: readonly LegacyKeyframe[]): PiecePlan | null {
  const k0 = kfs[0];
  if (k0 === undefined) return null;
  const k1 = kfs[1];
  if (k1 === undefined) {
    return { pieces: [], lead: null, final: { at: k0.time, value: k0.value }, nanLead: false };
  }
  const pieces: Piece[] = [];
  let lead: number | null = null;
  let nanLead = false;
  // Segment 0 is active on (-inf, t_1).
  const c01 = cmpTime(k0.time, k1.time);
  if (c01 < 0) pieces.push(pieceOf(k0, k1, k0.time, k1.time));
  else if (c01 === 0) {
    if (k0.tween === 'hold') lead = k0.value;
    else {
      // progress = (t - t0) / 0 -> +-inf: Phira shows NaN / infinity before t_1 (only visible when t_1 > 0).
      nanLead = Beats.cmp(k1.time.beat, Beats.ZERO) > 0;
      lead = k1.value;
    }
  } else if (Beats.cmp(k1.time.beat, Beats.ZERO) > 0) {
    // Out of order (t_0 > t_1): the segment's own line extends back over [0, t_1).
    pieces.push(pieceOf(k0, k1, BEAT_ZERO_POINT, k1.time));
  }
  let e = k1.time;
  for (let i = 1; i + 1 < kfs.length; i++) {
    const a = kfs[i];
    const b = kfs[i + 1];
    if (a === undefined || b === undefined) break;
    const start = e;
    const end = maxTime(e, b.time);
    e = end;
    if (cmpTime(start, end) >= 0) continue;
    pieces.push(pieceOf(a, b, start, end));
  }
  const last = kfs[kfs.length - 1] ?? k0;
  return { pieces, lead, final: { at: e, value: last.value }, nanLead };
}

// ---------------------------------------------------------------------------------------------------------------
// Pieces -> IR events
// ---------------------------------------------------------------------------------------------------------------

/** Largest finite f32. */
const F32_MAX = 3.4028234663852886e38;

/**
 * The value an f32 field holds after writing and reading back: the shortest decimal of Math.fround(v) (what the
 * RPE writer emits and the decoder reads), so imported values survive export -> import unchanged. Non-finite
 * values are clamped to the f32 range (NaN -> 0).
 */
export function canonicalF32(v: number): number {
  if (Number.isNaN(v)) return 0;
  const f = Math.fround(v);
  if (!Number.isFinite(f)) return f > 0 ? F32_MAX : -F32_MAX;
  return Number(shortestF32(f));
}

export function irEvent(
  start: Beat,
  end: Beat,
  v0: number,
  v1: number,
  easingType: number,
  origin: IrEventOrigin,
): IrEvent<number> {
  return {
    startTime: Beats.toTriple(start),
    endTime: Beats.toTriple(end),
    start: canonicalF32(v0),
    end: canonicalF32(v1),
    easingType,
    easingLeft: 0,
    easingRight: 1,
    bezier: 0,
    bezierPoints: [0, 0, 0, 0],
    linkgroup: 0,
    extra: [],
    origin,
    srcKey: null,
  };
}

/** A constant event of one beat holding v from `at` (from beat 0 when `at` is later). */
function constantFrom(at: Beat, v: number, origin: IrEventOrigin): IrEvent<number> {
  const start = Beats.cmp(at, Beats.ZERO) > 0 ? Beats.ZERO : at;
  const end = Beats.cmp(at, Beats.ZERO) > 0 ? Beats.add(at, Beats.ONE) : Beats.add(start, Beats.ONE);
  return irEvent(start, end, v, v, 1, origin);
}

/**
 * Events reproducing a plan (module header). `map` converts plan values (format units) to RPE units; it must be
 * affine so that linear interpolation commutes with it. Returns [] only for an empty plan.
 */
export function planToEvents(plan: PiecePlan | null, map: (v: number) => number = (v) => v): IrEvent<number>[] {
  if (plan === null) return [];
  const out: IrEvent<number>[] = [];
  for (const p of plan.pieces) {
    if (Beats.cmp(p.start.beat, p.end.beat) >= 0) continue;
    const v0 = map(p.v0);
    const v1 = p.constant ? v0 : map(p.v1);
    const prev = out[out.length - 1];
    // A hold that continues the previous end value is a gap: RPE keeps the previous event's end value.
    if (p.constant && prev !== undefined && fr(prev.end) === fr(v0)) continue;
    out.push(irEvent(p.start.beat, p.end.beat, v0, v1, p.constant ? 1 : p.easingType, 'segment'));
  }
  const finalValue = map(plan.final.value);
  const first = out[0];
  if (first === undefined) return [constantFrom(plan.final.at.beat, finalValue, 'single')];
  // Before the first event.
  const firstStart = Beats.fromTriple(first.startTime).beat;
  if (plan.lead !== null) {
    const leadValue = map(plan.lead);
    if (first.start === first.end && fr(first.start) === fr(leadValue)) {
      if (Beats.cmp(firstStart, Beats.ZERO) > 0) out[0] = { ...first, startTime: [0, 0, 1] };
    } else if (Beats.cmp(firstStart, Beats.ZERO) > 0) {
      out.unshift(irEvent(Beats.ZERO, firstStart, leadValue, leadValue, 1, 'lead'));
    }
  } else if (first.start === first.end && Beats.cmp(firstStart, Beats.ZERO) > 0) {
    // A constant first event extrapolates as a constant: starting it at beat 0 changes nothing.
    out[0] = { ...first, startTime: [0, 0, 1] };
  }
  // After the last event.
  const last = out[out.length - 1];
  if (last !== undefined && fr(last.end) !== fr(finalValue)) {
    const lastEnd = Beats.fromTriple(last.endTime).beat;
    const at = Beats.max(lastEnd, plan.final.at.beat);
    out.push(irEvent(at, Beats.add(at, Beats.ONE), finalValue, finalValue, 1, 'trailing'));
  }
  return out;
}

export function toEventList(events: readonly IrEvent<number>[]): IrEventList<number> | undefined {
  return events.length === 0 ? undefined : (events as unknown as NonEmpty<IrEvent<number>>);
}

// ---------------------------------------------------------------------------------------------------------------
// Height keyframes -> constant speed events
// ---------------------------------------------------------------------------------------------------------------

/** A floor-height keyframe (linear between keyframes; `hold` = the final freeze at max_time). */
export interface HeightKeyframe {
  readonly time: TimePoint;
  /** Height in the format's own units (PGR: value * s, PEC: cv * s). */
  readonly h: number;
  readonly hold: boolean;
  /** Exact slope to the next keyframe when known (avoids f64 noise for contiguous events). */
  readonly slope?: number;
}

export interface SpeedEvents {
  readonly events: IrEvent<number>[];
  /** Height discontinuities forward play produces (not representable by speed: approximated). */
  readonly jumps: number;
}

/**
 * Constant speed events whose floor integral reproduces Phira's forward-played height curve: segment slopes in the
 * format's speed units times `toRpe`. Equal neighbours merge; the height freeze after the last keyframe is a
 * trailing speed-0 event.
 */
export function heightToSpeedEvents(kfs: readonly HeightKeyframe[], toRpe: number): SpeedEvents {
  const k0 = kfs[0];
  if (k0 === undefined) return { events: [], jumps: 0 };
  const pieces: { start: TimePoint; end: TimePoint; slope: number }[] = [];
  let jumps = 0;
  const slopeOf = (a: HeightKeyframe, b: HeightKeyframe): number => {
    if (a.hold) return 0;
    if (a.slope !== undefined) return a.slope;
    const dt = b.time.sec - a.time.sec;
    return dt === 0 ? 0 : (b.h - a.h) / dt;
  };
  const heightAt = (a: HeightKeyframe, slope: number, t: number): number => a.h + slope * (t - a.time.sec);
  const near = (x: number, y: number): boolean => Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(x), Math.abs(y));
  let e: TimePoint = k0.time;
  let prevEndH = k0.h;
  const k1 = kfs[1];
  if (k1 !== undefined) {
    // Segment 0 is active on (-inf, t_1).
    const slope = slopeOf(k0, k1);
    const c = cmpTime(k0.time, k1.time);
    if (c < 0) pieces.push({ start: k0.time, end: k1.time, slope });
    else if (c > 0 && Beats.cmp(k1.time.beat, Beats.ZERO) > 0) {
      pieces.push({ start: BEAT_ZERO_POINT, end: k1.time, slope });
    }
    prevEndH = c === 0 ? k0.h : heightAt(k0, slope, k1.time.sec);
    e = k1.time;
  }
  for (let i = 1; i + 1 < kfs.length; i++) {
    const a = kfs[i];
    const b = kfs[i + 1];
    if (a === undefined || b === undefined) break;
    const slope = slopeOf(a, b);
    const start = e;
    const end = maxTime(e, b.time);
    e = end;
    if (cmpTime(start, end) >= 0) continue;
    const h0 = heightAt(a, slope, start.sec);
    if (!near(prevEndH, h0)) jumps++;
    prevEndH = heightAt(a, slope, end.sec);
    pieces.push({ start, end, slope });
  }
  // From E on Phira holds the last keyframe's height.
  const lastKf = kfs[kfs.length - 1];
  if (lastKf !== undefined && kfs.length > 1 && !near(prevEndH, lastKf.h)) jumps++;
  const out: IrEvent<number>[] = [];
  for (const p of pieces) {
    const v = p.slope * toRpe;
    const prev = out[out.length - 1];
    if (prev !== undefined && fr(prev.start) === fr(v) && Beats.eq(Beats.fromTriple(prev.endTime).beat, p.start.beat)) {
      out[out.length - 1] = { ...prev, endTime: Beats.toTriple(p.end.beat) };
      continue;
    }
    out.push(irEvent(p.start.beat, p.end.beat, v, v, 1, 'segment'));
  }
  const finalAt = e;
  const last = out[out.length - 1];
  if (last === undefined) return { events: [constantFrom(finalAt.beat, 0, 'single')], jumps };
  if (fr(last.end) !== 0) {
    const lastEnd = Beats.fromTriple(last.endTime).beat;
    const at = Beats.max(lastEnd, finalAt.beat);
    out.push(irEvent(at, Beats.add(at, Beats.ONE), 0, 0, 1, 'trailing'));
  }
  return { events: out, jumps };
}

// ---------------------------------------------------------------------------------------------------------------
// IR builders
// ---------------------------------------------------------------------------------------------------------------

/** A constant list from beat 0 to `end` (at least one beat; line scale). */
export function constantList(v: number, end: Beat = Beats.ONE): IrEventList<number> {
  return [irEvent(Beats.ZERO, Beats.max(end, Beats.ONE), v, v, 1, 'single')];
}

export interface LegacyLineInput {
  readonly alpha?: IrEventList<number> | undefined;
  readonly moveX?: IrEventList<number> | undefined;
  readonly moveY?: IrEventList<number> | undefined;
  readonly rotate?: IrEventList<number> | undefined;
  readonly speed?: IrEventList<number> | undefined;
  /** RPE scaleX (PEC line length); absent for PGR. */
  readonly scaleX?: number;
  /**
   * End of the constant scale events. Scale events count for RPE's max_time, so ending them where the source
   * format's max_time ends (minus its 1 s) makes the floor position freeze no earlier than Phira froze it.
   */
  readonly scaleEnd?: Beat;
  readonly notes: readonly IrNote[];
}

/** An imported legacy line: built-in texture, cover on, one event layer, thickness 1.0002. */
export function legacyLine(input: LegacyLineInput): IrLine {
  const layer: { -readonly [K in keyof IrLayer]: IrLayer[K] } = { nullKeys: [], extra: [] };
  if (input.alpha !== undefined) layer.alphaEvents = input.alpha;
  if (input.moveX !== undefined) layer.moveXEvents = input.moveX;
  if (input.moveY !== undefined) layer.moveYEvents = input.moveY;
  if (input.rotate !== undefined) layer.rotateEvents = input.rotate;
  if (input.speed !== undefined) layer.speedEvents = input.speed;
  const extended: { -readonly [K in keyof IrExtended]: IrExtended[K] } = {
    scaleYEvents: constantList(LEGACY_THICKNESS_SCALE_Y, input.scaleEnd),
    ignoredLists: [],
    nullKeys: [],
    extra: [],
  };
  if (input.scaleX !== undefined) extended.scaleXEvents = constantList(input.scaleX, input.scaleEnd);
  return {
    srcLine: null,
    Name: '',
    Texture: 'line.png',
    textureKind: 'builtin',
    father: -1,
    rotateWithFather: false,
    isCover: 1,
    zOrder: 0,
    attachUI: null,
    Group: 0,
    bpmfactor: 1,
    anchor: [0.5, 0.5],
    isGif: false,
    numOfNotes: input.notes.filter((n) => n.isFake === 0).length,
    eventLayers: [layer],
    extended,
    notes: input.notes,
    notesKey: 'present',
    extra: [],
  };
}

export interface LegacyNoteInput {
  /** RPE note type: 1 Tap, 2 Hold, 3 Flick, 4 Drag. */
  readonly type: 1 | 2 | 3 | 4;
  readonly start: Beat;
  readonly end: Beat;
  readonly positionX: number;
  readonly above: boolean;
  readonly fake: boolean;
  readonly speed: number;
  readonly size: number;
  readonly visibleTime?: number;
}

export function legacyNote(n: LegacyNoteInput): IrNote {
  return {
    type: n.type,
    startTime: Beats.toTriple(n.start),
    endTime: Beats.toTriple(n.type === 2 ? n.end : n.start),
    positionX: canonicalF32(n.positionX),
    above: n.above ? 1 : 2,
    isFake: n.fake ? 1 : 0,
    speed: canonicalF32(n.speed),
    yOffset: 0,
    size: canonicalF32(n.size),
    alpha: 255,
    visibleTime: n.visibleTime ?? RPE_DEFAULT_VISIBLE_TIME,
    extra: [],
    srcNote: null,
  };
}

export function legacyMeta(offsetMs: number): IrMeta {
  return {
    RPEVersion: LEGACY_RPE_VERSION,
    rpeVersionEffective: LEGACY_RPE_VERSION,
    offset: offsetMs,
    name: '',
    level: '',
    charter: '',
    composer: '',
    illustration: '',
    background: '',
    song: '',
    id: '',
    extra: [],
  };
}

export function irBpm(bpm: number, beat: Beat): IrBpm {
  return { bpm, startTime: Beats.toTriple(beat), extra: [] };
}

/**
 * Splits an offset in milliseconds into META.offset (i32 ms) and the remainder in seconds for info.yml `offset`
 * (P§0: META.offset is an integer).
 */
export function splitOffsetMs(totalMs: number): { readonly metaMs: number; readonly infoOffsetSec: number } {
  if (!Number.isFinite(totalMs)) return { metaMs: 0, infoOffsetSec: 0 };
  const metaMs = Math.min(Math.max(Math.round(totalMs), I32_MIN), I32_MAX);
  const rest = (totalMs - metaMs) / 1000;
  // Below a nanosecond the remainder is decimal-to-binary noise, not a real offset.
  return { metaMs: metaMs === 0 ? 0 : metaMs, infoOffsetSec: Math.abs(rest) < 1e-9 ? 0 : rest };
}

// ---------------------------------------------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------------------------------------------

interface Folded {
  readonly cls: ImportClass;
  message: MessageRef;
  readonly path: string | undefined;
  readonly line: number | undefined;
  count: number;
}

/**
 * Import report collector shared by the legacy importers: equal findings (class, key, line) fold into one entry
 * with a count; `phiraWouldLoad` turns false for anything Phira refuses or crashes on.
 */
export class LegacyReport {
  private readonly folded = new Map<string, Folded>();
  private readonly order: string[] = [];
  private loads = true;

  add(
    cls: ImportClass,
    key: string,
    params?: Readonly<Record<string, string | number>>,
    where: { readonly path?: string; readonly line?: number } = {},
    count = 1,
  ): void {
    // Fold by class, key, line and the path without list indices ("300 events of line 3" is one entry).
    const pattern = where.path === undefined ? '' : where.path.replace(/\[\d+\]/g, '[]');
    const id = `${cls}|${key}|${where.line === undefined ? '' : String(where.line)}|${pattern}`;
    const prev = this.folded.get(id);
    if (prev !== undefined) {
      prev.count += count;
      return;
    }
    const message: MessageRef = params === undefined ? { key } : { key, params };
    this.folded.set(id, { cls, message, path: where.path, line: where.line, count });
    this.order.push(id);
  }

  /** Phira itself would refuse or crash on the source. */
  fatal(
    key: string,
    params?: Readonly<Record<string, string | number>>,
    where?: { path?: string; line?: number },
  ): void {
    this.loads = false;
    this.add('changed', key, params, where);
  }

  get phiraWouldLoad(): boolean {
    return this.loads;
  }

  entries(): ImportReportEntry[] {
    const out: ImportReportEntry[] = [];
    for (const id of this.order) {
      const f = this.folded.get(id);
      if (f === undefined) continue;
      const entry: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] } = {
        cls: f.cls,
        message: f.message,
      };
      if (f.path !== undefined) entry.path = f.path;
      if (f.line !== undefined) entry.line = f.line;
      if (f.count > 1) entry.count = f.count;
      out.push(entry);
    }
    return out;
  }
}
